import { getMomentTz } from "@dexteel/mesf-core";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { CasterPourbackReason } from "../../../meltshop/models/Catalogs";
import { useDataEntryContext } from "../context/reducer.provider";
import { useTimerContext } from "../context/timer.provider";
import type { Probe, ProcessResponse, Strand } from "../models/caster.models";
import {
  changeCasterHeatStatus,
  deleteProbeById,
  getCasterProcess,
  reassignMold as reassignMoldRepository,
  upsertHeat,
  upsertPourback,
  upsertProbe,
  upsertProcess,
  upsertProcessData,
  upsertStrandData,
} from "../repositories/caster.repositories";
import { getBannerMissingFields } from "../utils/strand-losses/getBannerMissingFields";
import { getPendingStrandLossWarnings } from "../utils/strand-losses/getPendingStrandLossWarnings";

const moment = getMomentTz();

export const useCasterDataEntryActions = () => {
  const navigate = useNavigate();

  const {
    state,
    actions: {
      setDEProcess,
      setErrorMessage,
      setIsDataLoading,
      setIsSubmitLoading,
      setNotificationMessage,
      setLastUpdate,
    },
  } = useDataEntryContext();

  const { restart } = useTimerContext();

  const loadData = async (processId: number | null) => {
    setIsDataLoading(true);
    const resp = await getCasterProcess({ ProcessId: processId });
    if (resp.ok) {
      const data = resp.data as ProcessResponse;
      const process = data.tables["0"].rows[0];
      const strands = data.tables["1"].rows;
      const backendMissingFields = data.tables["8"]?.rows ?? [];
      const previousPendingStrandLosses = data.tables["9"]?.rows ?? [];
      const strandLossWarnings = getPendingStrandLossWarnings(
        previousPendingStrandLosses,
        strands,
      );
      setDEProcess({
        process,
        strands,
        probes: data.tables["2"].rows,
        tundishTemperatures: data.tables["3"].rows,
        strandSpeeds: data.tables["4"].rows,
        strandMoldLevels: data.tables["5"].rows,
        padTemperatures: data.tables["6"].rows,
        steelTemperatures: data.tables["7"].rows,
        missingFields: getBannerMissingFields(
          process,
          backendMissingFields,
          strandLossWarnings,
        ),
        previousPendingStrandLosses,
      });
      setLastUpdate();
      navigate(`/caster-data-entry/${process?.ProcessId}`);
    } else {
      setErrorMessage(resp.message!);
    }
    setIsDataLoading(false);
  };

  const saveProcessData = async ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");

    const processedValue = value instanceof Date ? value.toISOString() : value;

    const resp = await upsertProcessData({
      processId,
      field,
      value: processedValue,
    });

    setIsSubmitLoading(false);

    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveProcess = async ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");

    const processedValue = value instanceof Date ? value.toISOString() : value;

    const resp = await upsertProcess({
      processId,
      field,
      value: processedValue,
    });

    setIsSubmitLoading(false);

    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeat = async ({
    heatId,
    field,
    value,
  }: {
    heatId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertHeat({
      HeatId: heatId,
      field: field,
      value: value,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const refreshData = useCallback(async () => {
    if (state.deProcess?.process?.ProcessId) {
      await loadData(state.deProcess.process.ProcessId);
    } else {
      await loadData(null);
    }
    restart();
  }, [state.deProcess?.process?.ProcessId]);

  const savePourback = async (pourback: CasterPourbackReason) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertPourback({
      PourbackId: state.deProcess?.process!.PourbackId!,
      HeatId: state.deProcess?.process!.HeatId!,
      SourceProcessId: state.deProcess?.process!.ProcessId!,
      PourbackWeight: pourback.PourbackWeight,
      PourbackReasonId: pourback.PourbackReasonId,
      SourceTime: state.deProcess?.process!.LadleOpen || moment.utc().toDate(),
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Pourback saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveProbe = async (probe: Probe) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProbe({
      ProcessId: state.deProcess?.process!.ProcessId!,
      ProbeId: probe.ProbeId!,
      IsProbeManual: true,
      Timestamp: probe.Timestamp!.toISOString(),
      Temperature: probe.Temperature,
      Oxygen: probe.Oxygen,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Probe saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveStrand = async (strand: Strand) => {
    setIsSubmitLoading(true);
    setErrorMessage("");

    const firstCutTime =
      strand.FirstCutTime instanceof Date
        ? strand.FirstCutTime.toISOString()
        : null;
    const lastCutTime =
      strand.LastCutTime instanceof Date
        ? strand.LastCutTime.toISOString()
        : null;
    const resp = await upsertStrandData({
      ProcessId: state.deProcess?.process.ProcessId ?? null,
      Strand: strand.Strand,
      MoldId: strand.MoldId,
      FirstCutTime: firstCutTime,
      LastCutTime: lastCutTime,
      Pieces: strand.Pieces,
      CastTons: strand.CastTons,
      SpeedIPMAvg: strand.SpeedIPMAvg,
      WaterDeltaTAvg: strand.WaterDeltaTAvg,
      WaterFlowAvg: strand.WaterFlowAvg,
      WaterTempAvg: strand.WaterTempAvg,
      IsManual: true,
      IsForSale: strand.IsForSale,
      ShroudingMold: strand.ShroudingMold,
      BreakOut: strand.BreakOut,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Strand saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const changeHeatStatus = async (heatId: number, status: string) => {
    setIsSubmitLoading(true);
    const resp = await changeCasterHeatStatus({
      heatId: heatId,
      status: status,
    });
    if (resp.ok) {
      const data = resp.data;
      await loadData(data[0].ProcessId);
      return data;
    } else {
      setErrorMessage(resp.message!);
    }
    setIsSubmitLoading(false);
  };

  const reassignMold = async ({
    sourceStrand,
    targetStrand,
    moldId,
  }: {
    sourceStrand: number;
    targetStrand: number;
    moldId: number;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await reassignMoldRepository({
      ProcessId: state.deProcess?.process.ProcessId ?? 0,
      SourceStrand: sourceStrand,
      TargetStrand: targetStrand,
      MoldId: moldId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Mold reassigned successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteProbe = async (probeId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteProbeById({
      ProbeId: probeId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Probe deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  return {
    loadData,
    saveProcess,
    saveProcessData,
    saveHeat,
    saveProbe,
    saveStrand,
    savePourback,
    reassignMold,
    refreshData,
    changeHeatStatus,
    deleteProbe,
  };
};
